/**
 * Pure flow rules for the preferences questionnaire (no React, no side effects).
 *
 * Belongs here: back/advance/clear-answers decisions given cursor + questionnaire map.
 * Does not belong here: navigation calls, API submit, or UI.
 */
import type {
  FundPreferenceType,
  PreferencesAnswersMap,
  PreferencesClearAnswersOptions
} from '../setup/types'
import { PreferencesClearAnswersMode } from '../setup/types'
import type { PreferencesQuestionnaireMap } from '../../question-utils/preferencesMappers'
import type { Question } from '../../shared/types/questionnaire.types'
import { getVisibleQuestionsFromList, findNextVisibleQuestionIndex } from '../../question-utils/visibleQuestions'

// ─── Clear answers on edit ───────────────────────────────────────────────────

/**
 * Question ids to clear for a preference type.
 * Full type wipe (no `fromQuestionId`) uses every id in the type — visibility does not matter.
 * `fromQuestionId` = after-this cascade within that type's ordered list.
 */
export function getQuestionIdsToClearForPreferenceType(
  questionnaireMap: PreferencesQuestionnaireMap,
  preferenceTypeId: string,
  fromQuestionId?: string
): string[] {
  const entry = questionnaireMap[preferenceTypeId]
  if (!entry?.questionIds?.length) return []
  const ordered = entry.questionIds
  if (fromQuestionId == null) return [...ordered]
  const index = ordered.indexOf(fromQuestionId)
  return index === -1 ? [] : ordered.slice(index + 1)
}

/** Reverse lookup: which preference type owns this question id. */
export function getPreferenceTypeIdForQuestion(
  questionnaireMap: PreferencesQuestionnaireMap,
  questionId: string
): string | null {
  for (const [preferenceTypeId, entry] of Object.entries(questionnaireMap)) {
    if (entry?.questionIds?.includes(questionId)) return preferenceTypeId
  }
  return null
}

// MULTI_SELECT_GROUPS: missing after wipe/edit → no ids selected
function reseedMissingMultiSelectAnswers(
  answers: PreferencesAnswersMap,
  questionnaireMap: PreferencesQuestionnaireMap
): void {
  for (const entry of Object.values(questionnaireMap)) {
    for (const question of entry?.questions ?? []) {
      if (question?.input?.type !== 'MULTI_SELECT_GROUPS' || !question.id) continue
      if (answers[question.id] == null) {
        answers[question.id] = []
      }
    }
  }
}

/**
 * Copy committed answers, clear per edit mode, then reseed multi-select defaults.
 * Used by PreparePreferenceTypeEdit / BeginEditAll — not by updateDraftAnswer.
 */
export function getPreferencesAnswersAfterEdit(
  previousAnswers: PreferencesAnswersMap,
  mode: PreferencesClearAnswersMode,
  options: PreferencesClearAnswersOptions | undefined,
  questionnaireMap: PreferencesQuestionnaireMap
): PreferencesAnswersMap {
  let nextAnswers: PreferencesAnswersMap
  if (mode === PreferencesClearAnswersMode.ALL) {
    nextAnswers = {}
  } else {
    nextAnswers = { ...previousAnswers }
    if (mode === PreferencesClearAnswersMode.PREFERENCE_TYPE && options?.preferenceTypeId) {
      const questionIdsToClear = getQuestionIdsToClearForPreferenceType(
        questionnaireMap,
        options.preferenceTypeId,
        undefined
      )
      questionIdsToClear.forEach((clearedQuestionId) => delete nextAnswers[clearedQuestionId])
    } else if (mode === PreferencesClearAnswersMode.AFTER_THIS && options?.questionId) {
      const preferenceTypeId =
        options.preferenceTypeId ??
        getPreferenceTypeIdForQuestion(questionnaireMap, options.questionId)
      if (preferenceTypeId) {
        const questionIdsToClear = getQuestionIdsToClearForPreferenceType(
          questionnaireMap,
          preferenceTypeId,
          options.questionId
        )
        questionIdsToClear.forEach((clearedQuestionId) => delete nextAnswers[clearedQuestionId])
      }
    }
  }
  reseedMissingMultiSelectAnswers(nextAnswers, questionnaireMap)
  return nextAnswers
}

// Set-equality for multi-select arrays; strict === for single values
function isSamePreferenceAnswer(
  previous: PreferencesAnswersMap[string] | undefined,
  nextValue: PreferencesAnswersMap[string]
): boolean {
  if (Array.isArray(previous) && Array.isArray(nextValue)) {
    if (previous.length !== nextValue.length) return false
    const previousIds = new Set(previous)
    for (const optionId of nextValue) {
      if (!previousIds.has(optionId)) return false
    }
    return true
  }
  return previous === nextValue
}

/**
 * Write one answer into a copy; cascade-clear downstream questions when the value changed.
 * Shared by UpdateDraftAnswer and AnswerQuestion.
 */
export function getPreferencesAnswersAfterAdd(
  previousAnswers: PreferencesAnswersMap,
  questionId: string,
  value: string | number | string[],
  questionnaireMap: PreferencesQuestionnaireMap
): PreferencesAnswersMap {
  const nextAnswers = { ...previousAnswers, [questionId]: value }
  // Changed answer invalidates later questions in the same preference type
  if (questionId in previousAnswers && !isSamePreferenceAnswer(previousAnswers[questionId], value)) {
    const preferenceTypeId = getPreferenceTypeIdForQuestion(questionnaireMap, questionId)
    if (preferenceTypeId) {
      const questionIdsToClear = getQuestionIdsToClearForPreferenceType(
        questionnaireMap,
        preferenceTypeId,
        questionId
      )
      questionIdsToClear.forEach((clearedQuestionId) => delete nextAnswers[clearedQuestionId])
    }
  }
  reseedMissingMultiSelectAnswers(nextAnswers, questionnaireMap)
  return nextAnswers
}

/** Filter one preference type's questions by branch visibility for the given answers map. */
export function getVisibleQuestionsForPreferenceType(
  questionnaireMap: PreferencesQuestionnaireMap,
  preferenceTypeId: string,
  answers: PreferencesAnswersMap
): Question[] {
  const entry = questionnaireMap[preferenceTypeId]
  const questions = entry?.questions ?? []
  return getVisibleQuestionsFromList(questions, answers)
}

// ─── Back ────────────────────────────────────────────────────────────────────

export enum PreferencesBackTargetType {
  PrevQuestion = 'prev_question',
  PrevTypeLast = 'prev_type_last',
  BrowserBack = 'browser_back'
}

export type PreferencesBackTarget =
  | { type: PreferencesBackTargetType.PrevQuestion; preferenceTypeIndex: number; questionIndex: number }
  | { type: PreferencesBackTargetType.PrevTypeLast; preferenceTypeIndex: number; questionIndex: number }
  | { type: PreferencesBackTargetType.BrowserBack }

/** Where Back should land — prev question, prev type's last visible, or browser pop. */
export function getPreferencesBackTarget(
  questionIndex: number,
  preferenceTypeIndex: number,
  preferenceTypes: FundPreferenceType[],
  questionnaireMap: PreferencesQuestionnaireMap,
  answers: PreferencesAnswersMap
): PreferencesBackTarget {
  // Same type: previous visible question
  if (questionIndex > 0) {
    return {
      type: PreferencesBackTargetType.PrevQuestion,
      preferenceTypeIndex,
      questionIndex: questionIndex - 1
    }
  }
  if (preferenceTypeIndex > 0 && preferenceTypes.length > 0) {
    const previousType = preferenceTypes[preferenceTypeIndex - 1]
    if (previousType) {
      // First question in type: land on last visible question of previous type
      const previousVisible = getVisibleQuestionsForPreferenceType(
        questionnaireMap,
        previousType.id,
        answers
      )
      const lastQuestionIndex = Math.max(0, previousVisible.length - 1)
      return {
        type: PreferencesBackTargetType.PrevTypeLast,
        preferenceTypeIndex: preferenceTypeIndex - 1,
        questionIndex: lastQuestionIndex
      }
    }
  }
  return { type: PreferencesBackTargetType.BrowserBack }
}

// ─── Advance ─────────────────────────────────────────────────────────────────

export enum PreferencesNextAfterAnswerTargetType {
  NextQuestion = 'next_question',
  NextPreferenceType = 'next_preference_type',
  Review = 'review'
}

export type PreferencesNextAfterAnswerTarget =
  | {
      type: PreferencesNextAfterAnswerTargetType.NextQuestion
      preferenceTypeIndex: number
      questionIndex: number
    }
  | { type: PreferencesNextAfterAnswerTargetType.NextPreferenceType; nextPreferenceTypeIndex: number }
  | { type: PreferencesNextAfterAnswerTargetType.Review }

export interface GetPreferencesNextAfterAnswerParams {
  preferenceTypeIndex: number
  questionIndex: number
  currentQuestionId: string | undefined
  visibleQuestions: Question[]
  visibleAfterAnswer: Question[]
  isLastPreferenceType: boolean
}

/**
 * After AnswerQuestion writes the value — next visible question, next type, or review/allDone.
 * Called before dispatch so page-flow can build the matching Leave plan.
 */
export function getPreferencesNextAfterAnswer(
  params: GetPreferencesNextAfterAnswerParams
): PreferencesNextAfterAnswerTarget {
  const {
    preferenceTypeIndex,
    questionIndex,
    currentQuestionId,
    visibleQuestions,
    visibleAfterAnswer,
    isLastPreferenceType
  } = params

  // Re-evaluate visibility with the new answer — branch questions may appear or disappear
  const nextVisible = findNextVisibleQuestionIndex(
    visibleQuestions,
    visibleAfterAnswer,
    currentQuestionId,
    questionIndex
  )

  if (nextVisible != null) {
    return {
      type: PreferencesNextAfterAnswerTargetType.NextQuestion,
      preferenceTypeIndex,
      questionIndex: nextVisible.nextIndex
    }
  }

  if (!isLastPreferenceType) {
    // Exhausted current type — first question of next type (handler resets questionIndex)
    return {
      type: PreferencesNextAfterAnswerTargetType.NextPreferenceType,
      nextPreferenceTypeIndex: preferenceTypeIndex + 1
    }
  }

  return { type: PreferencesNextAfterAnswerTargetType.Review }
}
